'use client'

import { useState } from 'react'
import { operations } from '@/lib/marketing/content'

const moduleIcons = {
  bookings: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  members: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  marketing: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  payments: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  analytics: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
}

// UI Preview components for each module
function BookingsPreview() {
  return (
    <div className="bg-[#002C2B]/50 rounded-xl p-4 border border-[#E4FFE7]/[0.06]">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-white/50">Available Bays</div>
        <div className="text-[10px] text-[#71ED7F]">Live</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3].map((bay) => (
          <div key={bay} className={`p-2 rounded-lg text-center ${bay === 2 ? 'bg-[#71ED7F]/10 border border-[#71ED7F]/30' : 'bg-[#E4FFE7]/[0.04]'}`}>
            <div className="text-[10px] text-white/40">Bay {bay}</div>
            <div className={`text-xs font-medium ${bay === 2 ? 'text-[#71ED7F]' : 'text-white/60'}`}>
              {bay === 2 ? 'Booked' : 'Open'}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function MembersPreview() {
  return (
    <div className="bg-[#002C2B]/50 rounded-xl p-4 border border-[#E4FFE7]/[0.06]">
      <div className="text-xs text-white/50 mb-3">Recent Members</div>
      <div className="space-y-2">
        {[
          { initials: 'JD', name: 'John Davis', bookings: 8 },
          { initials: 'SM', name: 'Sarah Miller', bookings: 12 },
        ].map((member, i) => (
          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-[#E4FFE7]/[0.04]">
            <div className="w-7 h-7 rounded-full bg-[#71ED7F]/20 flex items-center justify-center text-[10px] text-[#71ED7F] font-medium">
              {member.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white/80 truncate">{member.name}</div>
              <div className="text-[10px] text-white/40">{member.bookings} bookings</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function MarketingPreview() {
  return (
    <div className="bg-[#002C2B]/50 rounded-xl p-4 border border-[#E4FFE7]/[0.06]">
      <div className="text-xs text-white/50 mb-3">Newsletter Draft</div>
      <div className="space-y-2">
        <div className="h-3 w-2/3 bg-white/10 rounded" />
        <div className="h-2 w-full bg-white/5 rounded" />
        <div className="h-2 w-4/5 bg-white/5 rounded" />
        <div className="flex gap-2 mt-3">
          <div className="h-6 w-16 bg-[#71ED7F]/20 rounded text-[10px] text-[#71ED7F] flex items-center justify-center">Send</div>
          <div className="h-6 w-16 bg-[#E4FFE7]/[0.06] rounded text-[10px] text-white/40 flex items-center justify-center">Preview</div>
        </div>
      </div>
    </div>
  )
}

function PaymentsPreview() {
  return (
    <div className="bg-[#002C2B]/50 rounded-xl p-4 border border-[#E4FFE7]/[0.06]">
      <div className="text-xs text-white/50 mb-3">Today's Revenue</div>
      <div className="text-2xl font-semibold text-white mb-2">$847</div>
      <div className="flex items-center gap-2 text-[10px]">
        <span className="text-[#71ED7F]">↑ 12%</span>
        <span className="text-white/40">vs last week</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full mt-3">
        <div className="h-full w-[65%] bg-gradient-to-r from-[#71ED7F] to-[#71ED7F]/50 rounded-full" />
      </div>
    </div>
  )
}

function AnalyticsPreview() {
  return (
    <div className="bg-[#002C2B]/50 rounded-xl p-4 border border-[#E4FFE7]/[0.06]">
      <div className="text-xs text-white/50 mb-3">Bay Utilization</div>
      <div className="flex items-end gap-1 h-16">
        {[40, 65, 80, 55, 90, 70, 45].map((h, i) => (
          <div key={i} className="flex-1 bg-[#71ED7F]/20 rounded-t" style={{ height: `${h}%` }}>
            <div className="w-full h-full bg-[#71ED7F]/40 rounded-t" style={{ height: `${h * 0.7}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[9px] text-white/30">
        <span>Mon</span>
        <span>Tue</span>
        <span>Wed</span>
        <span>Thu</span>
        <span>Fri</span>
        <span>Sat</span>
        <span>Sun</span>
      </div>
    </div>
  )
}

const previews = {
  bookings: BookingsPreview,
  members: MembersPreview,
  marketing: MarketingPreview,
  payments: PaymentsPreview,
  analytics: AnalyticsPreview,
}

type ModuleId = 'bookings' | 'members' | 'marketing' | 'payments' | 'analytics'

export function OperationsModules() {
  const [activeModule, setActiveModule] = useState<ModuleId>(operations.modules[0].id as ModuleId)

  const currentModule = operations.modules.find(m => m.id === activeModule)!
  const PreviewComponent = previews[activeModule as keyof typeof previews]

  return (
    <section id="features" className="py-16 sm:py-24">
      <div className="max-w-[960px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-block px-3 py-1 rounded-full bg-[#71ED7F]/10 border border-[#71ED7F]/20 text-[#71ED7F] text-xs font-semibold uppercase tracking-wider mb-4">
            {operations.badge}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium font-display mb-4">
            <span className="text-white">{operations.headline}</span>{' '}
            <span className="text-white/35">{operations.headlineAccent}</span>
          </h2>
          <p className="text-white/50 max-w-xl mx-auto text-sm sm:text-base">
            {operations.subheadline}
          </p>
        </div>

        {/* Module tabs - wrap on smaller screens, centered */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {operations.modules.map((module) => (
            <button
              key={module.id}
              onClick={() => setActiveModule(module.id as ModuleId)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                activeModule === module.id
                  ? 'bg-[#71ED7F]/10 border border-[#71ED7F]/30 text-[#71ED7F]'
                  : 'bg-[#001716] border border-[#E4FFE7]/[0.08] text-white/50 hover:text-white/70 hover:border-[#E4FFE7]/[0.12]'
              }`}
            >
              <span className={activeModule === module.id ? 'text-[#71ED7F]' : 'text-white/40'}>
                {moduleIcons[module.id as keyof typeof moduleIcons]}
              </span>
              {module.title}
            </button>
          ))}
        </div>

        {/* Active module content */}
        <div className="bg-[#001716] rounded-2xl border border-[#E4FFE7]/[0.08] p-6 sm:p-8 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)]">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left: Description */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#71ED7F]/10 border border-[#71ED7F]/20 flex items-center justify-center text-[#71ED7F]">
                  {moduleIcons[currentModule.id as keyof typeof moduleIcons]}
                </div>
                <h3 className="text-xl font-semibold text-white">{currentModule.title}</h3>
              </div>

              <p className="text-white/60 text-sm sm:text-base mb-6 leading-relaxed">
                {currentModule.description}
              </p>

              <div className="space-y-2.5">
                {currentModule.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-[#71ED7F] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-white/50">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: UI Preview */}
            <div className="relative">
              <div className="absolute -inset-3 bg-[#71ED7F]/[0.03] rounded-2xl blur-xl pointer-events-none" />
              <div className="relative">
                <PreviewComponent />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
